import { Component, OnDestroy, OnInit, ViewChildren, ElementRef, QueryList, ViewChild, NgZone, inject, Input } from '@angular/core';
import { Validators, FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { StoreService } from 'src/app/services/store.service';
import { ResponsiveLayoutService } from 'src/app/services/responsive-layout.service';
import { CommonService } from 'src/app/services/common.service';
import { Order } from 'src/app/models/order';
import { RutService } from 'src/app/services/rut.service';
import { User } from 'src/app/models/user';
import { StepService } from 'src/app/services/step.service';
import { NgbModal, NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
import { ModalAlertComponent } from 'src/app/shared/modal-alert/modal-alert.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { ReCaptchaV3Service } from 'ng-recaptcha-2';
import { API_KEY } from 'src/app/shared/utils/recaptcha';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es-CL';
//import { MapsAPILoader } from '@agm/core';
import { REGIONES_COMUNA_PAGO } from 'src/app/shared/utils/regiones';
import { SwalAlertService } from 'src/app/services/swal-alert.service';
import { resolveBillingAddressFromGeocoder } from 'src/app/shared/utils/billing-address-geocode.util';
import { AuthenticationService } from 'src/app/services/authentication.service';
registerLocaleData(localeEs, 'es');

type HormigonPaymentProvider = 'mercadopago' | 'gnet' | 'flow';

export const DEFAULT_HORMIGON_PAYMENT_PROVIDER: HormigonPaymentProvider = 'mercadopago';

export const HORMIGON_PAYMENT_PROVIDERS: ReadonlyArray<{
  id: HormigonPaymentProvider;
  name: string;
  description: string;
  logo: string;
}> = [
  {
    id: 'mercadopago',
    name: 'Mercado Pago',
    description: 'Transferencias, tarjetas de crédito y débito',
    logo: 'assets/img/logos/mercadopago.svg',
  },
  {
    id: 'gnet',
    name: 'Getnet',
    description: 'Tarjetas de crédito y débito',
    logo: 'assets/img/logos/getnet-logo.svg',
  },
  {
    id: 'flow',
    name: 'Flow',
    description: 'Tarjetas de crédito y débito',
    logo: 'assets/img/logos/flow.svg',
  },
];

@Component({
  selector: 'app-pago',
  templateUrl: './pago.component.html',
  styleUrls: ['./pago.component.css']
})
export class PagoComponent implements OnInit, OnDestroy {
  @Input() resumeMode = false;
  @ViewChildren('transactionFrom') transactionFrom?: QueryList<ElementRef>;
  @ViewChild('alertModal', { static: true }) alertModal?: ElementRef;
  @ViewChild('paymentModal', { static: true }) paymentModal?: ElementRef;
  @ViewChild('selectMap', { static: true }) selectMap?: ElementRef;
  swal = inject(SwalAlertService);
  form!: FormGroup;
  formAddress!: FormGroup;
  order?: Order;
  transaction = {
    url: '',
    token: ''
  };
  siteKey = API_KEY;
  geocoder = new google.maps.Geocoder();
  usarMismaDireccion = false
  readonly paymentProviders = HORMIGON_PAYMENT_PROVIDERS;
  selectedPaymentProvider: HormigonPaymentProvider = DEFAULT_HORMIGON_PAYMENT_PROVIDER;
  private mercadoPagoConfirmationStartedAt = 0;
  private mercadoPagoWebhookGraceSeconds = 20;
  private mercadoPagoConfirmationTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly spinner: NgxSpinnerService,
    private readonly commomService: CommonService,
    private readonly storeService: StoreService,
    private readonly rutService: RutService,
    private readonly stepService: StepService,
    private readonly modalService: NgbModal,
    private readonly config: NgbModalConfig,
    private readonly reCaptchaV3Service: ReCaptchaV3Service,
    private readonly authenticationService: AuthenticationService,
    public responsiveService: ResponsiveLayoutService,
    private readonly ngZone: NgZone
  ) {
    this.config.backdrop = 'static';
    this.config.keyboard = false;
  }

  regionesComunas = REGIONES_COMUNA_PAGO;
  get comunaByRegion() {
    const region = this.formAddress?.get('region')?.value;
    if (region) {
      return REGIONES_COMUNA_PAGO.find(r => r.nombreRegion == region)?.comunas;
    }
    return [];
  }

  checkAddressPart(address: any, part: any) {
    const check = address.find((x: any) => x.types.find((y: any) => y === part));
    return check;
  }

  async ngOnInit() {
    this.route.params.subscribe(params => {
      const orderCode = params['id'];
      const orderId = params['orderId'];
      const isConfirmation = this.route.snapshot.url.some(segment => segment.path.includes('confirmacion'));
      const isCancellation = this.route.snapshot.url.some(segment => segment.path.includes('cancelar'));
      if (isConfirmation && this.getPaymentProvider() === 'mercadopago') {
        this.startMercadoPagoConfirmation(orderId);
        return;
      }
      if (isConfirmation || isCancellation) {
        this.handlePaymentNotification(orderId, isConfirmation, this.getPaymentProvider());
        return;
      }
      if (orderCode) {
        this.spinner.show();
        this.storeService.get_cotizacion(orderCode).subscribe({
          next: (e) => {
            if (e.cotizacion) {
              e.sujeta_factibilidad = e.sujeta_factibilidad === true || (e as { sujeta_factibilidad?: unknown }).sujeta_factibilidad === 'Si';
              this.commomService.currentOrder = e;
              this.order = this.commomService.currentOrder;
              this.initForm();
              this.spinner.hide();
            } else {
              this.spinner.hide();
              this.cotizacionAlert();
            }
          }
        });
      } else {
        this.order = this.commomService.currentOrder;
        if (this.order && !this.resumeMode) {
          this.order.cotizacion = false;
        }
        this.commomService.currentOrder = this.order;
      }
    });
    this.stepService.changeStep(5);
    this.initForm();
  }

  ngOnDestroy(): void {
    this.clearMercadoPagoConfirmationTimer();
  }

  private startMercadoPagoConfirmation(resumeToken: string): void {
    if (!resumeToken) {
      this.navigateToMercadoPagoOrderSummary(resumeToken);
      return;
    }
    this.spinner.show();
    this.mercadoPagoConfirmationStartedAt = Date.now();
    this.storeService.get_mercadopago_confirmation_config(resumeToken).subscribe({
      next: (config) => {
        this.mercadoPagoWebhookGraceSeconds = config.webhook_grace_seconds;
        this.checkMercadoPagoConfirmation(resumeToken);
      },
      error: () => this.returnToMercadoPagoOrderSummary(resumeToken),
    });
  }

  private checkMercadoPagoConfirmation(resumeToken: string): void {
    this.storeService.post_mercadopago_confirmation_status(resumeToken).subscribe({
      next: (response) => {
        if (response.final) {
          this.returnToMercadoPagoOrderSummary(resumeToken);
          return;
        }
        const elapsedSeconds = (Date.now() - this.mercadoPagoConfirmationStartedAt) / 1000;
        if (elapsedSeconds >= this.mercadoPagoWebhookGraceSeconds) {
          this.returnToMercadoPagoOrderSummary(resumeToken);
          return;
        }
        const retryAfterMilliseconds = Math.max(2000, response.retry_after_seconds * 1000);
        this.mercadoPagoConfirmationTimer = setTimeout(
          () => this.checkMercadoPagoConfirmation(resumeToken),
          retryAfterMilliseconds,
        );
      },
      error: () => this.returnToMercadoPagoOrderSummary(resumeToken),
    });
  }

  returnToMercadoPagoOrderSummary(resumeToken: string): void {
    this.clearMercadoPagoConfirmationTimer();
    this.storeService.post_payment_confirmation({ order_code: resumeToken }, 'mercadopago').subscribe({
      next: (response) => {
        if (response.redirect_url) {
          this.spinner.hide();
          location.href = response.redirect_url;
          return;
        }
        this.navigateToMercadoPagoOrderSummary(resumeToken);
      },
      error: () => this.navigateToMercadoPagoOrderSummary(resumeToken),
    });
  }

  private navigateToMercadoPagoOrderSummary(resumeToken: string): void {
    this.clearMercadoPagoConfirmationTimer();
    this.spinner.hide();
    if (!resumeToken) {
      this.router.navigate(['/']);
      return;
    }
    this.router.navigate(['/pedido', resumeToken]);
  }

  private clearMercadoPagoConfirmationTimer(): void {
    if (this.mercadoPagoConfirmationTimer) {
      clearTimeout(this.mercadoPagoConfirmationTimer);
      this.mercadoPagoConfirmationTimer = undefined;
    }
  }

  handlePaymentNotification(orderCode: string, isConfirmation: boolean, provider: 'gnet' | 'mercadopago' = 'gnet') {
    this.spinner.show();
    const data = this.buildPaymentNotificationPayload(orderCode, provider);
    const request = isConfirmation
      ? this.storeService.post_payment_confirmation(data, provider)
      : this.storeService.post_payment_cancellation(data, provider);

    request.subscribe({
      next: (response) => {
        this.spinner.hide();
        if (response.redirect_url) {
          location.href = response.redirect_url;
        } else {
          const title = isConfirmation ? 'Pago Confirmado' : 'Pago Cancelado';
          const description = isConfirmation
            ? 'El pago se ha procesado exitosamente.'
            : 'El pago ha sido cancelado. Si necesita más información, contáctenos.';
          this.showModal(title, description, () => {
            this.router.navigate(['/']);
          });
        }
      },
      error: (err) => {
        this.spinner.hide();
        console.error('Error en el proceso de notificación', err);
        this.showModal('Error', 'Hubo un error al procesar la solicitud. Inténtelo más tarde.', () => {
          this.router.navigate(['/']);
        });
      }
    });
  }

  private buildPaymentNotificationPayload(orderCode: string, provider: 'gnet' | 'mercadopago') {
    if (provider !== 'mercadopago') {
      return {
        order_code: orderCode,
      };
    }

    const queryParams = this.route.snapshot.queryParams;
    return {
      order_code: orderCode,
      status: queryParams['status'] ?? '',
      collection_status: queryParams['collection_status'] ?? '',
      payment_id: queryParams['payment_id'] ?? '',
      collection_id: queryParams['collection_id'] ?? '',
      external_reference: queryParams['external_reference'] ?? '',
      payment_type: queryParams['payment_type'] ?? '',
      merchant_order_id: queryParams['merchant_order_id'] ?? '',
      preference_id: queryParams['preference_id'] ?? '',
    };
  }

  showModal(title: string, description: string, onClose?: () => void) {
    const modalRef = this.modalService.open(ModalAlertComponent, { centered: true });
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.description = description;
    modalRef.componentInstance.headerCloseRunsFunction = true;

    if (onClose) {
      modalRef.componentInstance.function = () => {
        this.modalService.dismissAll();
        onClose();
      };
    } else {
      modalRef.componentInstance.function = () => {
        this.modalService.dismissAll();
      };
    }
  }

  initForm() {
    this.form = this.fb.group({
      email: [this.order?.user?.email, [Validators.required, Validators.pattern('[a-z0-9._%+-]+@[a-z0-9.-]+.[a-z]{2,3}$')]],
      name: [this.order?.user?.name, [Validators.required]],
      giro: [this.order?.user?.giro, [Validators.required]],
      rut: [this.order?.user?.rut, [Validators.required]],
      address: [this.order?.user?.address, [Validators.required]],
      helper: [this.order?.user?.address],
      phone: [this.order?.user?.phone, [Validators.required]],
      check: ['', [this.checkTerms]],
      mismadireccion: [false],
    });
    this.formAddress = this.fb.group({
      street: ['', Validators.required],
      number: ['', Validators.required],
      comuna: ['', Validators.required],
      region: ['', Validators.required],
    });
  }

  checkTerms(control: FormControl) {
    return (control.value) ? null : { check: true };
  }

  checkRUT() {
    const rutControl = this.form?.get('rut');
    const cleanValue = this.rutService.rutClean(rutControl?.value);
    const valid = this.rutService.getRutChile(0, cleanValue);

    if (valid) {
      rutControl?.setErrors(null);
      rutControl?.setValue(this.rutService.rutFormat(cleanValue));
    } else {
      rutControl?.setErrors({ noEsIgual: true });
    }
  }

  private validate(): boolean {
    const pooseeDatos = (this.form?.get('address')?.value ?? '').trim().length > 0;
    if (!pooseeDatos) {
      this.showFacturacionAddressAlert();
    }
    if (this.form?.invalid) {
      Object.values(this.form.controls).forEach(control => {
        if (control instanceof FormGroup) {
          Object.values(control.controls).forEach(c => c.markAsTouched());
        } else {
          control.markAsTouched();
        }
      });
      return false;
    }
    const data = this.form?.value;
    delete data.helper;
    this.commomService.setUserData(data as User);
    return true;
  }

  continuarPago() {
    this.flow();
  }

  webpay() {
    this.modalService.dismissAll();
    this.spinner.show();
    this.reCaptchaV3Service.execute('webpay').subscribe((token: string) => {
      const order = this.commomService.currentOrder;
      order.token = token;

      this.storeService.post_webpay_transaction(order).subscribe({
        next: (response) => {
          const form = this.transactionFrom?.first.nativeElement;
          this.transaction = { url: response.url, token: response.token };
          form.action = response.url;
          form[0].value = response.token;
          this.spinner.hide();
          form.submit();
        },
        error: () => {
          this.spinner.hide();
        }
      });
    }, error => {
      this.spinner.hide();
    });
  }

  flow() {
    this.modalService.dismissAll();
    this.spinner.show();

    this.reCaptchaV3Service.execute('flow').subscribe((token: string) => {
      const order = this.commomService.currentOrder;
      order.token = token;

      this.storeService.post_flow_transaction(order).subscribe({
        next: (response) => {
          location.href = `${response.url}?token=${response.token}`;
          this.spinner.hide();
        },
        error: (response) => {
          this.spinner.hide();
        }
      });
    }, error => {
      this.spinner.hide();
    });
  }


  gnet() {
    this.modalService.dismissAll();
    this.spinner.show();
    this.reCaptchaV3Service.execute('gnet').subscribe((token: string) => {
      const order = this.commomService.currentOrder;
      order.token = token;
      this.storeService.post_gnet_transaction(order).subscribe({
        next: (response) => {
          location.href = `${response.url}?token=${response.token}`;
          this.spinner.hide();
        },
        error: (response) => {
          this.spinner.hide();
        }
      });
    }, error => {
      this.spinner.hide();
    });
  }

  mercadopago() {
    this.modalService.dismissAll();
    this.spinner.show();
    this.reCaptchaV3Service.execute('mercadopago').subscribe((token: string) => {
      const order = this.commomService.currentOrder;
      order.token = token;
      this.storeService.post_mercadopago_transaction(order).subscribe({
        next: (response) => {
          this.spinner.hide();
          location.href = response.url;
        },
        error: () => {
          this.spinner.hide();
          this.showModal(
            'Error',
            'No pudimos iniciar el pago con Mercado Pago. Inténtelo nuevamente.',
            () => this.router.navigate(['/pago'])
          );
        }
      });
    }, () => {
      this.spinner.hide();
    });
  }


  // guardar() {
  //   if (this.validate()) {
  //     this.flow();
  //   }
  // }

  async guardar() {
    if (this.validate()) {
      if (await this.validarFactibilidad()) {
        if (this.resumeMode) {
          this.startSelectedPayment();
          return;
        }
        this.selectedPaymentProvider = DEFAULT_HORMIGON_PAYMENT_PROVIDER;
        this.modalService.open(this.paymentModal, {
          size: 'md',
          centered: true,
          windowClass: 'payment-selector-modal',
        });
      }
    }
  }

  selectPaymentProvider(provider: HormigonPaymentProvider) {
    this.selectedPaymentProvider = provider;
  }

  selectAndStartPayment(provider: HormigonPaymentProvider) {
    this.selectedPaymentProvider = provider;
    this.startSelectedPayment();
  }

  startSelectedPayment() {
    if (this.selectedPaymentProvider === 'flow') {
      this.flow();
      return;
    }
    if (this.selectedPaymentProvider === 'mercadopago') {
      this.mercadopago();
      return;
    }
    this.gnet();
  }

  private getPaymentProvider(): 'gnet' | 'mercadopago' {
    const provider = String(this.route.snapshot.queryParamMap.get('provider') ?? '').trim().toLowerCase();
    return provider === 'mercadopago' ? 'mercadopago' : 'gnet';
  }

  async validarFactibilidad() {
    const order = this.commomService.currentOrder;
    if (order.sujeta_factibilidad) {
      return await this.swal.confirmarTexto(
        'Por factibilidad técnica, pedido quedará sujeto a confirmación de despacho',
        'warning',
        {
          title: 'Aviso',
          confirmButtonText: 'OK',
          reverseButtons: true,
          focusCancel: true,
        }
      );
    }
    return true;
  }

  async cotizacion() {
    if (!this.validate()) return;
    if (!(await this.validarFactibilidad())) return;
    this.spinner.show();
    this.reCaptchaV3Service.execute('contact').subscribe({
      next: (token: string) => {
        const order = this.commomService.currentOrder;
        order.token = token;
        const adminUserId = this.authenticationService.adminUserId;
        if (
          order.user != null &&
          typeof adminUserId === 'number'
        ) {
          order.user = { ...order.user, auth_user_id: adminUserId };
        }
        this.storeService.post_cotizacion(order).subscribe({
          next: () => {
            this.spinner.hide();
            this.showAlert();
          },
          error: () => {
            this.spinner.hide();
          }
        });
      },
      error: () => {
        this.spinner.hide();
      }
    });
  }


  countTruck(i: number) {
    return Array(Math.ceil(i / 7.5));
  }

  showFacturacionAddressAlert() {
    this.form?.get('address')?.setValue('');
    const modalRef = this.modalService.open(ModalAlertComponent, { centered: true });
    modalRef.componentInstance.title = 'dirección facturación';
    modalRef.componentInstance.description = 'Su dirección de facturación es incompleta. Asegure seleccionar una de las opciones disponibles';
  }

  private finishQuoteModalsAndGoHome(): void {
    this.modalService.dismissAll();
    this.commomService.clearCheckoutDraft();
    this.stepService.changeStep(0);
    void this.router.navigate(['/']);
  }

  showAlert() {
    const modalRef = this.modalService.open(ModalAlertComponent, { centered: true });
    modalRef.componentInstance.title = 'cotización enviada';
    modalRef.componentInstance.description = 'Su cotización será enviada a su correo.';
    modalRef.componentInstance.headerCloseRunsFunction = true;
    modalRef.componentInstance.function = () => {
      this.finishQuoteModalsAndGoHome();
    };
  }

  cotizacionAlert() {
    const modalRef = this.modalService.open(ModalAlertComponent, { centered: true });
    modalRef.componentInstance.title = 'cotización';
    modalRef.componentInstance.description = 'Su cotización se encuentra vencida o es incorrecta.';
    modalRef.componentInstance.headerCloseRunsFunction = true;
    modalRef.componentInstance.function = () => {
      this.finishQuoteModalsAndGoHome();
    };
  }


  checkAddress(place: google.maps.GeocoderResult, address: string | null = null) {
    this.form?.get('helper')?.setValue('');
    this.form?.get('address')?.setValue('');
    const userLine = address ?? '';
    const outcome = resolveBillingAddressFromGeocoder(place, userLine);
    if (outcome.status === 'ok') {
      this.form?.get('helper')?.setValue(outcome.displayAddress);
      this.form?.get('address')?.setValue(outcome.billingAddress);
      return;
    }
    this.form?.get('helper')?.setErrors({ noValid: true });
  }

  guardarAddress() {
    let formValue = this.formAddress?.value;

    this.form?.get('mismadireccion').setValue(false);
    this.usarMismaDireccion = false;
    const address = `${formValue.street} ${formValue.number}, ${formValue.comuna}, ${formValue.region}`;
    let request =
    {
      address: address
    };

    this.geocoder?.geocode(request, (results: any, status: any) => {
      if (status === google.maps.GeocoderStatus.OK) {
        let result = results[0];
        this.checkAddress(result, address);
      } else {
        this.form?.get('address')?.setValue('');
        this.form?.get('helper')?.setValue('');
        this.form?.get('helper')?.setErrors({ noValid: true });
      }
      this.modalService.dismissAll();
    });
  }

  openModal(content: any) {
    this.modalService.open(content, { centered: true, backdrop: true, size: 'lg' });
  }
  modificarDireccion() {
    let direccion = '';

    if (!this.usarMismaDireccion) {
      direccion = this.order?.map?.address
    }
    this.usarMismaDireccion = !this.usarMismaDireccion;
    this.form?.get('helper')?.setValue(direccion)
    this.form?.get('address')?.setValue(direccion)
  }

  getResumeDispatchAddress(): string {
    const rawAddress = this.order?.map?.address?.trim();
    if (!rawAddress) {
      return '';
    }
    const parts = rawAddress
      .split(',')
      .map(part => part.trim())
      .filter(Boolean);
    if (parts.length >= 3) {
      const country = parts[parts.length - 1]?.toLowerCase();
      const hasCountry = country === 'chile';
      const comunaIndex = hasCountry ? parts.length - 3 : parts.length - 2;
      const regionIndex = hasCountry ? parts.length - 2 : parts.length - 1;
      const comuna = parts[comunaIndex]?.replace(/^\d+\s*/, '') ?? '';
      const region = parts[regionIndex] ?? '';
      return [comuna, region].filter(Boolean).join(', ');
    }
    if (parts.length >= 2) {
      const comuna = parts[parts.length - 2]?.replace(/^\d+\s*/, '') ?? '';
      const region = parts[parts.length - 1] ?? '';
      return [comuna, region].filter(Boolean).join(', ');
    }
    return rawAddress;
  }
}
